/* Run24 — PLP: filtros, orden y paginación vía Section Rendering API.
   Mismo patrón que facets.js de Dawn (fetch ?section_id= + history.pushState
   + popstate) pero con contrato de markup propio (clases r24- del prototipo).

   Contrato:
   - <r24-plp data-section-id> envuelve todo el contenido de la sección.
   - Un solo <form> con los inputs de filtros; el <select name="sort_by">
     participa vía atributo form="".
   - Enlaces que re-renderizan (chips activos, limpiar, paginación):
     [data-r24-facet-link]; con [data-r24-scroll-top] hacen scroll arriba.
   - Drawer móvil: [data-r24-filters-open] / [data-r24-filters-close];
     estado = clase .drawer-open en <r24-plp>.
   Sin JS todo degrada a URLs con recarga completa. */

(function () {
  'use strict';

  if (customElements.get('r24-plp')) return;

  var CACHE_MAX = 24;

  class R24Plp extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.initialParams = window.location.search.slice(1);

      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      var self = this;
      this.debouncedUpdate = this.debounce(function () {
        self.updateFromForm();
      }, 500);
    }

    connectedCallback() {
      this.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
      document.addEventListener('keydown', this.onKeyDown);

      // Cerrar el drawer si el viewport cruza al layout desktop (>1100)
      var self = this;
      this.desktopQuery = window.matchMedia('(min-width: 1101px)');
      this.onDesktop = function (event) {
        if (event.matches && self.classList.contains('drawer-open')) self.closeDrawer();
      };
      this.desktopQuery.addEventListener('change', this.onDesktop);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onDesktop);
      this.setBodyLock(false);
    }

    /* ── Eventos ── */

    onInput(event) {
      var target = event.target;
      if (target.matches('input[type="number"]')) {
        this.debouncedUpdate();
      } else if (target.matches('input[type="checkbox"], select[name="sort_by"]')) {
        this.updateFromForm(target);
      }
    }

    onClick(event) {
      var link = event.target.closest('[data-r24-facet-link]');
      if (link) {
        event.preventDefault();
        var qIndex = link.href.indexOf('?');
        var params = qIndex === -1 ? '' : link.href.slice(qIndex + 1);
        this.render(params, {
          updateHistory: true,
          scrollTop: link.hasAttribute('data-r24-scroll-top'),
        });
        return;
      }

      if (event.target.closest('[data-r24-filters-open]')) {
        this.openDrawer();
        return;
      }
      if (event.target.closest('[data-r24-filters-close]')) {
        this.closeDrawer();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.classList.contains('drawer-open')) {
        this.closeDrawer();
      }
    }

    onPopState(event) {
      var params =
        event.state && typeof event.state.r24Params === 'string'
          ? event.state.r24Params
          : this.initialParams;
      this.render(params, { updateHistory: false });
    }

    /* ── Estado → URL ── */

    updateFromForm(originInput) {
      var form = this.querySelector('form');
      if (!form) return;
      var formData = new FormData(form);
      var params = new URLSearchParams();
      formData.forEach(function (value, key) {
        if (value !== '') params.append(key, value);
      });
      this.render(params.toString(), {
        updateHistory: true,
        focusTarget: originInput || document.activeElement,
      });
    }

    render(searchParams, options) {
      options = options || {};
      var self = this;
      var url =
        window.location.pathname +
        '?section_id=' +
        this.dataset.sectionId +
        (searchParams ? '&' + searchParams : '');

      this.classList.add('is-loading');

      var focusKey = this.focusKeyFor(options.focusTarget);
      var openStates = this.detailsOpenStates();

      var request = this.cache.has(url)
        ? Promise.resolve(this.cache.get(url))
        : fetch(url)
            .then(function (response) {
              if (!response.ok) throw new Error('Run24 PLP: ' + response.status);
              return response.text();
            })
            .then(function (html) {
              if (self.cache.size >= CACHE_MAX) {
                self.cache.delete(self.cache.keys().next().value);
              }
              self.cache.set(url, html);
              return html;
            });

      request
        .then(function (html) {
          self.swap(html, openStates, focusKey);
          if (options.updateHistory) {
            history.pushState(
              { r24Params: searchParams },
              '',
              window.location.pathname + (searchParams ? '?' + searchParams : '')
            );
          }
          if (options.scrollTop) {
            var top = self.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: top, behavior: 'auto' });
          }
        })
        .catch(function (error) {
          console.error(error);
          // Fallback: navegación completa para no dejar la UI a medias.
          window.location.href =
            window.location.pathname + (searchParams ? '?' + searchParams : '');
        })
        .finally(function () {
          self.classList.remove('is-loading');
        });
    }

    swap(html, openStates, focusKey) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('r24-plp[data-section-id="' + this.dataset.sectionId + '"]');
      if (!fresh) return;
      this.innerHTML = fresh.innerHTML;

      // Restaurar grupos colapsados por el usuario
      this.querySelectorAll('details[data-r24-param]').forEach(function (details) {
        var param = details.getAttribute('data-r24-param');
        if (param in openStates) details.open = openStates[param];
      });

      // Devolver el foco al control que originó el cambio
      if (focusKey) {
        var selector = focusKey.value
          ? '[name="' + CSS.escape(focusKey.name) + '"][value="' + CSS.escape(focusKey.value) + '"]'
          : '[name="' + CSS.escape(focusKey.name) + '"]';
        var match = this.querySelector(selector);
        if (match) match.focus({ preventScroll: true });
      }
    }

    detailsOpenStates() {
      var states = {};
      this.querySelectorAll('details[data-r24-param]').forEach(function (details) {
        states[details.getAttribute('data-r24-param')] = details.open;
      });
      return states;
    }

    focusKeyFor(element) {
      if (!element || !element.name || !this.contains(element)) return null;
      return {
        name: element.name,
        value: element.type === 'checkbox' ? element.value : '',
      };
    }

    /* ── Drawer móvil ── */

    openDrawer() {
      this.classList.add('drawer-open');
      this.setBodyLock(true);
      var closeButton = this.querySelector('.r24-filters__drawerclose');
      if (closeButton) closeButton.focus();
    }

    closeDrawer() {
      this.classList.remove('drawer-open');
      this.setBodyLock(false);
      var openButton = this.querySelector('[data-r24-filters-open]');
      if (openButton) openButton.focus({ preventScroll: true });
    }

    setBodyLock(locked) {
      document.body.style.overflow = locked ? 'hidden' : '';
    }

    /* ── Utilidades ── */

    debounce(fn, wait) {
      var timer;
      return function () {
        var args = arguments;
        var context = this;
        clearTimeout(timer);
        timer = setTimeout(function () {
          fn.apply(context, args);
        }, wait);
      };
    }
  }

  customElements.define('r24-plp', R24Plp);
})();
